import { computed, Injectable, signal } from '@angular/core';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { ApiFilterRequest, FilterConfig } from './list-management.types';

/**
 * FilterManager gestisce i filtri con Signals
 * Automaticamente ricostruisce i filtri quando cambiano i form controls
 */
@Injectable({ providedIn: 'root' })
export class FilterManager {
  private readonly _activeFilters = signal<ApiFilterRequest[]>([]);
  private readonly _filterConfigs = signal<FilterConfig[]>([]);
  private readonly _isDirty = signal(false);

  readonly activeFilters$ = computed(() => this._activeFilters());
  readonly filterCount$ = computed(() => this._activeFilters().length);
  readonly isDirty$ = computed(() => this._isDirty());
  readonly hasFilters$ = computed(() => this._activeFilters().length > 0);

  registerFilters(configs: FilterConfig[]): void {
    this._filterConfigs.set(configs);
    this._setupFilterListeners();
  }

  private _setupFilterListeners(): void {
    const configs = this._filterConfigs();

    configs.forEach((config) => {
      config.control.statusChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
        this._rebuildFilters();
        this._isDirty.set(true);
      });

      config.control.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
        this._rebuildFilters();
        this._isDirty.set(true);
      });
    });
  }

  private _rebuildFilters(): void {
    const configs = this._filterConfigs();
    const filters: ApiFilterRequest[] = [];

    configs.forEach((config) => {
      const value = config.control.value;
      if (value !== null && value !== undefined && value !== '') {
        const transformedValue = config.transformer ? config.transformer(value) : value;

        filters.push({
          field: config.field,
          operator: config.operator || 'eq',
          value: transformedValue
        });
      }
    });

    this._activeFilters.set(filters);
  }

  resetFilter(field: string): void {
    const configs = this._filterConfigs();
    const config = configs.find((c) => c.field === field);
    if (config) {
      config.control.reset();
    }
  }

  resetAllFilters(): void {
    const configs = this._filterConfigs();
    configs.forEach((config) => {
      config.control.reset();
    });
    this._activeFilters.set([]);
    this._isDirty.set(false);
  }

  toggleFilter(field: string): void {
    const filters = this._activeFilters();
    const index = filters.findIndex((f) => f.field === field);
    if (index >= 0) {
      filters.splice(index, 1);
      this._activeFilters.set([...filters]);
    }
  }

  markClean(): void {
    this._isDirty.set(false);
  }
}
